import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import styled from "styled-components"
import { notificationsApi, type PortalNotification } from "./notificationsApi"

const Wrap = styled.div`
  position: relative;
`

const Trigger = styled.button`
  position: relative;
  min-height: 42px;
  padding: 0 0.9rem;
  border: 1px solid #c0ceda;
  border-radius: 2px;
  background: #fff;
  color: #173a5d;
  font: inherit;
  font-size: 0.82rem;
  font-weight: 750;
  cursor: pointer;

  span {
    position: absolute;
    top: -7px;
    right: -7px;
    min-width: 21px;
    height: 21px;
    display: grid;
    place-items: center;
    padding: 0 5px;
    border: 2px solid #fff;
    background: #c75f2f;
    color: #fff;
    font-size: 0.66rem;
  }

  @media (max-width: 520px) {
    width: 42px;
    padding: 0;
    overflow: hidden;
    color: transparent;

    &::after {
      content: "•";
      position: absolute;
      inset: 0;
      display: grid;
      place-items: center;
      color: #173a5d;
      font-size: 1.6rem;
    }
  }
`

const Panel = styled.section`
  position: absolute;
  top: calc(100% + 0.65rem);
  right: 0;
  z-index: 80;
  width: min(390px, calc(100vw - 2rem));
  border: 1px solid #bccbd7;
  background: #fff;
  box-shadow: 0 24px 60px rgba(8, 35, 58, 0.2);

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem 1.1rem;
    border-bottom: 1px solid #d2dde5;
  }

  h2 {
    margin: 0;
    color: #102f4b;
    font-size: 1rem;
  }

  header button {
    border: 0;
    background: transparent;
    color: #2b6b9e;
    font: inherit;
    font-size: 0.72rem;
    font-weight: 750;
    cursor: pointer;
  }
`

const Items = styled.div`
  max-height: 430px;
  overflow: auto;

  > p {
    margin: 0;
    padding: 1.4rem 1.1rem;
    color: #6b7f90;
    font-size: 0.84rem;
  }
`

const Item = styled(Link)<{ $unread: boolean }>`
  display: block;
  padding: 0.95rem 1.1rem;
  border-bottom: 1px solid #e0e7ec;
  border-left: 3px solid ${({ $unread }) => ($unread ? "#337abd" : "transparent")};
  background: ${({ $unread }) => ($unread ? "#f2f8fc" : "#fff")};
  color: inherit;
  text-decoration: none;

  strong {
    display: block;
    color: #173953;
    font-size: 0.84rem;
  }

  p {
    margin: 0.3rem 0 0;
    color: #657b8d;
    font-size: 0.78rem;
    line-height: 1.45;
  }

  time {
    display: block;
    margin-top: 0.45rem;
    color: #82919e;
    font-size: 0.68rem;
  }
`

const NotificationMenu = () => {
  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<PortalNotification[]>([])

  useEffect(() => {
    notificationsApi
      .list()
      .then(setItems)
      .catch(() => undefined)
  }, [])

  const unread = items.filter((item) => !item.readAt).length

  const markAll = async () => {
    await notificationsApi.markAllRead().catch(() => undefined)
    setItems((current) => current.map((item) => ({ ...item, readAt: item.readAt || new Date().toISOString() })))
  }

  const openItem = (item: PortalNotification) => {
    const id = item.id || item._id
    if (!item.readAt && id) {
      void notificationsApi.markRead(id)
      setItems((current) => current.map((candidate) => (candidate === item ? { ...candidate, readAt: new Date().toISOString() } : candidate)))
    }
    setOpen(false)
  }

  return (
    <Wrap>
      <Trigger type="button" aria-expanded={open} aria-controls="portal-notifications" onClick={() => setOpen((value) => !value)}>
        Avis
        {unread > 0 && <span>{unread > 9 ? "9+" : unread}</span>}
      </Trigger>
      {open && (
        <Panel id="portal-notifications" aria-label="Avis récents">
          <header>
            <h2>Avis récents</h2>
            {unread > 0 && <button onClick={markAll}>Tout marquer comme lu</button>}
          </header>
          <Items>
            {items.length ? (
              items.map((item) => (
                <Item key={item.id || item._id} to={item.href} $unread={!item.readAt} onClick={() => openItem(item)}>
                  <strong>{item.title}</strong>
                  <p>{item.body}</p>
                  <time dateTime={item.createdAt}>
                    {new Intl.DateTimeFormat("fr-CA", { dateStyle: "medium", timeStyle: "short" }).format(new Date(item.createdAt))}
                  </time>
                </Item>
              ))
            ) : (
              <p>Aucun nouvel avis.</p>
            )}
          </Items>
        </Panel>
      )}
    </Wrap>
  )
}

export default NotificationMenu
